import { httpCoreService } from "../services/HttpCoreService";
import type { ApiResponse } from "../types/auth.type";
import type {
  StudySiteAssignment,
  CreateStudySiteAssignmentRequest,
  UpdateStudySiteAssignmentRequest,
  StudySiteAssignmentListResponse,
  StudySiteAssignmentFilters,
  StudySiteAssignmentStatistics,
} from "../pages/StudyManagement/types/studySiteAssignment.types";

export interface AssignSiteToStudyRequest {
  siteId: string;
  siteNumber?: string;
  siteContactIds?: string[];
  principalInvestigatorIds?: string[];
  targetEnrollment?: number;
  country?: string;
  plannedActivationDate?: string;
  budgetStatus?: string;
  contractStatus?: string;
  notes?: string;
}

export interface AssignStudyToSiteRequest {
  studyId: string;
  siteNumber?: string;
  siteContactIds?: string[];
  principalInvestigatorIds?: string[];
  targetEnrollment?: number;
  country?: string;
  plannedActivationDate?: string;
  budgetStatus?: string;
  contractStatus?: string;
  notes?: string;
}

export interface IStudySiteAssignmentRepository {
  // Study site assignment management
  createAssignment(
    assignment: CreateStudySiteAssignmentRequest,
  ): Promise<StudySiteAssignment>;
  getAssignments(
    studyId: string,
    filters?: StudySiteAssignmentFilters,
    page?: number,
    limit?: number,
  ): Promise<StudySiteAssignmentListResponse>;
  getAssignmentsByStudy(studyId: string): Promise<StudySiteAssignment[]>;
  getAssignmentsBySite(siteId: string): Promise<StudySiteAssignment[]>;
  getAssignmentById(id: string): Promise<StudySiteAssignment>;
  updateAssignment(
    id: string,
    updates: UpdateStudySiteAssignmentRequest,
  ): Promise<StudySiteAssignment>;
  deleteAssignment(id: string): Promise<void>;

  // Status management
  activateAssignment(id: string): Promise<StudySiteAssignment>;
  deactivateAssignment(id: string): Promise<StudySiteAssignment>;
  completeAssignment(id: string): Promise<StudySiteAssignment>;

  // Bulk operations
  assignSitesToStudy(
    studyId: string,
    siteAssignments: AssignSiteToStudyRequest[],
  ): Promise<StudySiteAssignment[]>;
  assignStudiesToSite(
    siteId: string,
    studyAssignments: (string | AssignStudyToSiteRequest)[],
  ): Promise<StudySiteAssignment[]>;
  removeSiteFromStudy(studyId: string, siteId: string): Promise<void>;
  removeStudyFromSite(siteId: string, studyId: string): Promise<void>;

  // Statistics
  getAssignmentStatistics(
    studyId: string,
  ): Promise<StudySiteAssignmentStatistics>;
}

export class StudySiteAssignmentRepository implements IStudySiteAssignmentRepository {
  async createAssignment(
    assignment: CreateStudySiteAssignmentRequest,
  ): Promise<StudySiteAssignment> {
    const response: ApiResponse<StudySiteAssignment> =
      await httpCoreService.post<
        StudySiteAssignment,
        CreateStudySiteAssignmentRequest
      >("/study-site-assignments", assignment);

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to create study site assignment",
      );
    }

    if (!response.data) {
      throw new Error(
        "Study site assignment creation succeeded but no data was returned.",
      );
    }

    return response.data;
  }

  async getAssignments(
    studyId: string,
    filters?: StudySiteAssignmentFilters,
    page = 1,
    limit = 10,
  ): Promise<StudySiteAssignmentListResponse> {
    const queryParams = new URLSearchParams({
      studyId,
      page: page.toString(),
      limit: limit.toString(),
      ...(filters?.status && { status: filters.status }),
      ...(filters?.principalInvestigator && {
        pi: filters.principalInvestigator,
      }),
      ...(filters?.search && { search: filters.search }),
    });

    const response: ApiResponse<StudySiteAssignmentListResponse> =
      await httpCoreService.get(`/study-site-assignments?${queryParams}`);

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to fetch study site assignments",
      );
    }

    if (!response.data) {
      throw new Error("No study site assignments data was returned.");
    }

    return response.data;
  }

  async getAssignmentsByStudy(studyId: string): Promise<StudySiteAssignment[]> {
    const response: ApiResponse<StudySiteAssignment[]> =
      await httpCoreService.get(`/study-site-assignments/by-study/${studyId}`);

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to fetch study site assignments",
      );
    }

    if (!response.data) {
      throw new Error("No study site assignments data was returned.");
    }

    return response.data;
  }

  async getAssignmentsBySite(siteId: string): Promise<StudySiteAssignment[]> {
    const response: ApiResponse<StudySiteAssignment[]> =
      await httpCoreService.get(`/study-site-assignments/by-site/${siteId}`);

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to fetch site study assignments",
      );
    }

    if (!response.data) {
      throw new Error("No site study assignments data was returned.");
    }

    return response.data;
  }

  async getAssignmentById(id: string): Promise<StudySiteAssignment> {
    const response: ApiResponse<StudySiteAssignment> =
      await httpCoreService.get(`/study-site-assignments/${id}`);

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to fetch study site assignment",
      );
    }

    if (!response.data) {
      throw new Error("No study site assignment data was returned.");
    }

    return response.data;
  }

  async updateAssignment(
    id: string,
    updates: UpdateStudySiteAssignmentRequest,
  ): Promise<StudySiteAssignment> {
    const response: ApiResponse<StudySiteAssignment> =
      await httpCoreService.put<
        StudySiteAssignment,
        UpdateStudySiteAssignmentRequest
      >(`/study-site-assignments/${id}`, updates);

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to update study site assignment",
      );
    }

    if (!response.data) {
      throw new Error(
        "Study site assignment update succeeded but no data was returned.",
      );
    }

    return response.data;
  }

  async deleteAssignment(id: string): Promise<void> {
    const response: ApiResponse<void> = await httpCoreService.delete(
      `/study-site-assignments/${id}`,
    );

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to delete study site assignment",
      );
    }
  }

  async activateAssignment(id: string): Promise<StudySiteAssignment> {
    const response: ApiResponse<StudySiteAssignment> =
      await httpCoreService.put(`/study-site-assignments/${id}/activate`);

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to activate study site assignment",
      );
    }

    if (!response.data) {
      throw new Error(
        "Study site assignment activation succeeded but no data was returned.",
      );
    }

    return response.data;
  }

  async deactivateAssignment(id: string): Promise<StudySiteAssignment> {
    const response: ApiResponse<StudySiteAssignment> =
      await httpCoreService.put(`/study-site-assignments/${id}/deactivate`);

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to deactivate study site assignment",
      );
    }

    if (!response.data) {
      throw new Error(
        "Study site assignment deactivation succeeded but no data was returned.",
      );
    }

    return response.data;
  }

  async completeAssignment(id: string): Promise<StudySiteAssignment> {
    const response: ApiResponse<StudySiteAssignment> =
      await httpCoreService.put(`/study-site-assignments/${id}/complete`);

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to complete study site assignment",
      );
    }

    if (!response.data) {
      throw new Error(
        "Study site assignment completion succeeded but no data was returned.",
      );
    }

    return response.data;
  }

  async assignSitesToStudy(
    studyId: string,
    siteAssignments: AssignSiteToStudyRequest[],
  ): Promise<StudySiteAssignment[]> {
    const requestBody = {
      studyId,
      siteAssignments: siteAssignments.map((assignment) => ({
        studyId,
        siteId: assignment.siteId,
        ...(assignment.siteNumber ? { siteNumber: assignment.siteNumber } : {}),
        ...(assignment.principalInvestigatorIds &&
        assignment.principalInvestigatorIds.length > 0
          ? {
              siteContactIds:
                assignment.siteContactIds &&
                assignment.siteContactIds.length > 0
                  ? assignment.siteContactIds
                  : assignment.principalInvestigatorIds,
              principalInvestigatorIds: assignment.principalInvestigatorIds,
            }
          : {}),
        ...(assignment.targetEnrollment !== undefined
          ? { targetEnrollment: assignment.targetEnrollment }
          : {}),
        ...(assignment.country ? { country: assignment.country } : {}),
        ...(assignment.plannedActivationDate
          ? { plannedActivationDate: assignment.plannedActivationDate }
          : {}),
        ...(assignment.budgetStatus
          ? { budgetStatus: assignment.budgetStatus }
          : {}),
        ...(assignment.contractStatus
          ? { contractStatus: assignment.contractStatus }
          : {}),
        ...(assignment.notes ? { notes: assignment.notes } : {}),
      })),
    };

    const response: ApiResponse<StudySiteAssignment[]> =
      await httpCoreService.post<StudySiteAssignment[], typeof requestBody>(
        "/study-site-assignments/assign-sites-to-study",
        requestBody,
      );

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to assign sites to study",
      );
    }

    if (!response.data) {
      throw new Error(
        "Sites assigned successfully but no data was returned from the server.",
      );
    }

    return response.data;
  }

  async assignStudiesToSite(
    siteId: string,
    studyAssignments: (string | AssignStudyToSiteRequest)[],
  ): Promise<StudySiteAssignment[]> {
    const requestBody = {
      siteId,
      studyAssignments: studyAssignments.map((assignment) => {
        if (typeof assignment === "string") {
          return {
            studyId: assignment,
            siteId,
          };
        }

        return {
          studyId: assignment.studyId,
          siteId,
          ...(assignment.siteNumber
            ? { siteNumber: assignment.siteNumber }
            : {}),
          ...(assignment.principalInvestigatorIds &&
          assignment.principalInvestigatorIds.length > 0
            ? {
                siteContactIds:
                  assignment.siteContactIds &&
                  assignment.siteContactIds.length > 0
                    ? assignment.siteContactIds
                    : assignment.principalInvestigatorIds,
                principalInvestigatorIds: assignment.principalInvestigatorIds,
              }
            : {}),
          ...(assignment.targetEnrollment !== undefined
            ? { targetEnrollment: assignment.targetEnrollment }
            : {}),
          ...(assignment.country ? { country: assignment.country } : {}),
          ...(assignment.plannedActivationDate
            ? { plannedActivationDate: assignment.plannedActivationDate }
            : {}),
          ...(assignment.budgetStatus
            ? { budgetStatus: assignment.budgetStatus }
            : {}),
          ...(assignment.contractStatus
            ? { contractStatus: assignment.contractStatus }
            : {}),
          ...(assignment.notes ? { notes: assignment.notes } : {}),
        };
      }),
    };

    const response: ApiResponse<StudySiteAssignment[]> =
      await httpCoreService.post<StudySiteAssignment[], typeof requestBody>(
        "/study-site-assignments/assign-studies-to-site",
        requestBody,
      );

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to assign studies to site",
      );
    }

    if (!response.data) {
      throw new Error(
        "Studies assigned successfully but no data was returned from the server.",
      );
    }

    return response.data;
  }

  async getAssignmentStatistics(
    studyId: string,
  ): Promise<StudySiteAssignmentStatistics> {
    const response: ApiResponse<StudySiteAssignmentStatistics> =
      await httpCoreService.get(
        `/study-site-assignments/statistics?studyId=${studyId}`,
      );

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to fetch study site assignment statistics",
      );
    }

    if (!response.data) {
      throw new Error("No statistics data was returned.");
    }

    return response.data;
  }

  async removeSiteFromStudy(studyId: string, siteId: string): Promise<void> {
    const queryParams = new URLSearchParams({
      studyId,
      siteId,
    });

    const response: ApiResponse<void> = await httpCoreService.delete(
      `/study-site-assignments/by-site-and-study?${queryParams}`,
    );

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to remove site from study",
      );
    }
  }

  async removeStudyFromSite(siteId: string, studyId: string): Promise<void> {
    const queryParams = new URLSearchParams({
      studyId,
      siteId,
    });

    const response: ApiResponse<void> = await httpCoreService.delete(
      `/study-site-assignments/by-site-and-study?${queryParams}`,
    );

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to remove study from site",
      );
    }
  }
}

// Singleton instance
export const studySiteAssignmentRepository =
  new StudySiteAssignmentRepository();
